import { useCallback, useEffect, useRef, useState } from 'react';

export type Loaded<T> =
  | { status: 'loading'; data: undefined; error: undefined }
  | { status: 'error'; data: undefined; error: unknown }
  | { status: 'ready'; data: T; error: undefined };

/**
 * Run `load` on mount and whenever `key` changes; `reload` runs it again (the
 * page states' retry). `refresh` re-runs it quietly for polling: the data on
 * screen stays until new data arrives, and a failed refresh keeps it. A
 * response that arrives after a newer request started is dropped, so a slow
 * load can't overwrite a fresh one.
 */
export function useLoad<T>(
  load: () => Promise<T>,
  key: string,
): Loaded<T> & { reload: () => void; refresh: () => void } {
  const [state, setState] = useState<Loaded<T>>({
    status: 'loading',
    data: undefined,
    error: undefined,
  });
  const loadRef = useRef(load);
  loadRef.current = load;
  const generation = useRef(0);

  const run = useCallback(() => {
    const mine = ++generation.current;
    setState({ status: 'loading', data: undefined, error: undefined });
    loadRef.current().then(
      (data) => {
        if (mine === generation.current) setState({ status: 'ready', data, error: undefined });
      },
      (error: unknown) => {
        if (mine === generation.current) setState({ status: 'error', data: undefined, error });
      },
    );
  }, []);

  const refresh = useCallback(() => {
    const mine = ++generation.current;
    loadRef.current().then(
      (data) => {
        if (mine === generation.current) setState({ status: 'ready', data, error: undefined });
      },
      () => {
        // A failed refresh keeps what's on screen; the next one may succeed.
      },
    );
  }, []);

  useEffect(() => {
    run();
  }, [run, key]);

  return { ...state, reload: run, refresh };
}
